/**
 * MESP PWA Install — centralised hook + helpers (Feb 2026 revamp).
 *
 * Goals (per product spec):
 *   • Detect whether the user is running MESP as an installed PWA.
 *   • If not installed:
 *       - On every login, the dashboard surfaces a welcome modal that
 *         invites the user to install MESP. The same modal is also
 *         reachable from the sidebar at any time via the "تثبيت التطبيق"
 *         entry.
 *       - When the browser supports the W3C `beforeinstallprompt`
 *         event (Chrome/Edge/Samsung on Android & Desktop), we capture
 *         the event and trigger the native prompt straight from our
 *         CTA — no extra clicks.
 *       - When the browser does NOT support programmatic installation
 *         (iOS Safari, iOS Chromium, Desktop Safari, Firefox, …) we
 *         render rich, device-specific manual instructions.
 *   • "لاحقاً" hides the prompt for 24 hours via localStorage.
 *   • Once installation is detected (display-mode standalone OR the
 *     `appinstalled` event), we never auto-prompt again on this device.
 *
 * No backend touchpoint — this is purely a frontend nudge UX.
 */

export const DISMISS_KEY    = 'mesp_pwa_install_dismissed_at';
export const INSTALLED_KEY  = 'mesp_pwa_installed';
export const SESSION_SHOWN  = 'mesp_pwa_shown_this_session';
export const COOLDOWN_HOURS = 24;

export const PWA_OPEN_EVENT = 'mesp:pwa:open-install';

// ─────────────────────────────────────────────────────────────────
// Environment / standalone-mode detection
// ─────────────────────────────────────────────────────────────────
export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // Modern API (Chrome, Edge, Android, macOS Safari ≥17, …).
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari legacy flag.
  if (window.navigator.standalone === true) return true;
  // Android TWA / Trusted Web Activity referrer hint.
  if (document.referrer?.startsWith('android-app://')) return true;
  return false;
};

export const isLikelyInstalled = () => {
  if (isStandalone()) return true;
  try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; }
};

// ─────────────────────────────────────────────────────────────────
// User-Agent based platform / browser detection.
// We deliberately rely on UA strings (not UA-Client-Hints) because
// Safari and Firefox don't ship those headers in 2026 yet.
// ─────────────────────────────────────────────────────────────────
const ua = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');

export const isIOS = () => {
  const u = ua();
  // iPad on iPadOS 13+ identifies as Mac; combine with maxTouchPoints.
  const iPadOS13Plus = /Macintosh/.test(u) && typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 1;
  return /iPad|iPhone|iPod/.test(u) || iPadOS13Plus;
};
export const isAndroid = () => /Android/i.test(ua());
export const isMac     = () => /Macintosh|Mac OS X/.test(ua()) && !isIOS();
export const isWindows = () => /Windows NT/.test(ua());

export const isSafari = () => {
  const u = ua();
  return /Safari/.test(u) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg|OPR|SamsungBrowser/.test(u);
};
export const isChromeDesktop = () => /Chrome/.test(ua()) && !/Edg|OPR|SamsungBrowser/.test(ua()) && !isAndroid() && !isIOS();
export const isEdge = () => /Edg\//.test(ua()) || /EdgiOS/.test(ua());
export const isFirefox = () => /Firefox|FxiOS/.test(ua());
export const isSamsung = () => /SamsungBrowser/.test(ua());

/**
 * Resolve the most useful "platform" key for instruction rendering.
 * Order matters: most specific first so iOS Chromium doesn't fall into
 * the generic Chromium bucket.
 */
export const detectPlatform = () => {
  if (isIOS()) {
    if (isSafari()) return 'ios-safari';
    // Chrome / Edge / Firefox on iOS all use WebKit and share the same
    // share-sheet path. We bucket them separately so we can mention the
    // Safari recommendation in the copy.
    return 'ios-other';
  }
  if (isAndroid()) {
    if (isFirefox()) return 'android-firefox';
    if (isSamsung()) return 'android-samsung';
    return 'android-chromium'; // Chrome, Edge, Opera, Brave, …
  }
  // Desktops
  if (isMac() && isSafari()) return 'mac-safari';
  if (isFirefox()) return 'desktop-firefox';
  if (isEdge())    return 'desktop-edge';
  if (isChromeDesktop()) return 'desktop-chrome';
  return 'desktop-other';
};

/**
 * True when the current browser is expected to fire
 * `beforeinstallprompt`. Used purely as an optimistic hint while we
 * wait for the real event. We always still gate the native prompt on
 * `deferredPrompt` actually being captured.
 */
export const browserSupportsNativeInstall = (p = detectPlatform()) =>
  p === 'android-chromium' ||
  p === 'android-samsung'  ||
  p === 'desktop-chrome'   ||
  p === 'desktop-edge';

// ─────────────────────────────────────────────────────────────────
// Cooldown helpers (24 hours after "لاحقاً")
// ─────────────────────────────────────────────────────────────────
export const isOnCooldown = () => {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    return (Date.now() - parseInt(ts, 10)) < COOLDOWN_HOURS * 60 * 60 * 1000;
  } catch { return false; }
};
export const persistDismissal = () => {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
};
export const persistInstalled = () => {
  try {
    localStorage.setItem(INSTALLED_KEY, '1');
    localStorage.removeItem(DISMISS_KEY);
  } catch { /* ignore */ }
};

// ─────────────────────────────────────────────────────────────────
// Public event helper — anywhere in the app can call openInstallPrompt()
// to surface the modal without caring about the cooldown.
// ─────────────────────────────────────────────────────────────────
export const openInstallPrompt = () => {
  try {
    window.dispatchEvent(new CustomEvent(PWA_OPEN_EVENT));
  } catch { /* ignore */ }
};
